import { useCallback, useEffect, useMemo, useState } from 'react';
import { EMPTY_FILTERS } from '../constants';
import { JournalFilters, OptionField, OptionPick } from '../types';

const SEARCH_DELAY_MS = 300;

/**
 * Фильтры журнала - общие для таблицы, KPI и всех графиков.
 *  - effective: применённые фильтры (поиск - с задержкой); объект меняется только при реальной смене фильтров,
 *    поэтому набор символов в поиске не перезапускает загрузки;
 *  - «Сброс» не трогает выбранный период.
 */
export function useJournalFilters() {
  const [filters, setFilters] = useState<JournalFilters>(EMPTY_FILTERS);
  const [searchApplied, setSearchApplied] = useState('');

  useEffect(() => {
    if (filters.search === searchApplied) return undefined;
    const timer = window.setTimeout(() => setSearchApplied(filters.search), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [filters.search, searchApplied]);

  const { month, pharmacies, bucket, expiryFrom, expiryTo, picks } = filters;
  const effective = useMemo<JournalFilters>(
    () => ({ month, pharmacies, bucket, expiryFrom, expiryTo, picks, search: searchApplied }),
    [month, pharmacies, bucket, expiryFrom, expiryTo, picks, searchApplied]
  );

  const patchFilters = useCallback((patch: Partial<JournalFilters>) => setFilters(prev => ({ ...prev, ...patch })), []);

  const setPick = useCallback((field: OptionField, pick: OptionPick | null) => {
    setFilters(prev => ({ ...prev, picks: { ...prev.picks, [field]: pick } }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(prev => ({ ...EMPTY_FILTERS, month: prev.month }));
    setSearchApplied('');
  }, []);

  return { filters, effective, patchFilters, setPick, resetFilters };
}
